import React, { Fragment, useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { Tabs, Tab, InputAdornment } from "@mui/material";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../components/Admin/Reusable/Breadcrumb";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import { fetchSectionById, updateSection } from "../actions/section";
import SubmitButtonForm from "../components/Admin/Reusable/Form/SubmitButtonForm";
import FormTitleMain from "../components/Admin/Reusable/FormTitleMain";
import InputField from "../components/Admin/Reusable/InputField";
import languagesItems from "../utils/languages";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import { Editor } from "ckeditor5-custom-build/build/ckeditor";

import { TabPanel, a11yProps } from "../helper/TabPanel";
import SelectStatus from "../components/Admin/Reusable/Form/SelectStatus";

import AlertBox from "../components/Admin/Reusable/AlertBox";
import { useSelector } from "react-redux";
import { basicFormValidationHandler, formatOnlyDateDB } from "../utils";
import AmountInputField from "../components/Admin/Reusable/AmountInputField";

const SectionAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();

  const [activeLocaleTab, setActiveLocaleTab] = useState(0);
  const handleChangeLocaleTab = (e, newValue) => setActiveLocaleTab(newValue);
  const [errors, setErrors] = useState({});
  const { sectionDetail } = useSelector((state) => state.sectionReducer);
  const { userFormList } = useSelector((state) => state.userReducer);

  //locales
  const [locales, setLocales] = useState(() =>
    Object.fromEntries(
      languagesItems.map((l) => [
        l.code,
        { id: null, name: "", description: "", language: l.code },
      ])
    )
  );

  const initialState = {
    type: "KAT",
    price: "",
    discountPrice: "",
    discountStart: "",
    discountEnd: "",
    accessDuration: 30,
    shoppable: true,
    free: false,
  };

  //osnovi podaci
  const [data, setData] = useState(initialState);

  //breadcrumbs
  const translateForm = "sectionForm";
  const translateModule = "section";
  const pageType = id ? "Edit" : "Add";
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  useEffect(() => {
    if (!id) {
      // Ako `id` ne postoji, očisti stanje za novu kreaciju
      setData(initialState);
    } else {
      // Ako `id` postoji, učitaj podatke za korisnika
      dispatch(fetchSectionById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (sectionDetail && id) {
      setData({
        ...sectionDetail,
        discountEnd: formatOnlyDateDB(sectionDetail?.discountEnd),
        discountStart: formatOnlyDateDB(sectionDetail?.discountStart),
      });

      // popuni locales iz backend-a
      const filledLocales = Object.fromEntries(
        languagesItems.map((l) => {
          const found = sectionDetail.locales?.find(
            (loc) =>
              loc.language === l.code || loc.language === l.label.toUpperCase()
          );
          return [
            l.code,
            {
              id: found?.id || null,
              name: found?.name || "",
              description: found?.description || "",
              language: l.code,
            },
          ];
        })
      );

      setLocales(filledLocales);
    }
  }, [sectionDetail, id, dispatch]);

  const userValidationHandler = () => {
    let errorsObject = {};

    // univerzalno obavezna polja
    errorsObject = {
      ...errorsObject,
      ...basicFormValidationHandler({}),
    };

    // price je obavezan samo ako je shoppable === true
    if (data.shoppable) {
      errorsObject = {
        ...errorsObject,
        ...basicFormValidationHandler({
          price: data.price,
          accessDuration: data.accessDuration,
        }),
      };
    }

    // discount polja su obavezna samo ako je discount === true
    if (data.discount) {
      errorsObject = {
        ...errorsObject,
        ...basicFormValidationHandler({
          discountPrice: data.discountPrice,
          discountStart: data.discountStart,
        }),
      };
    }

    setErrors(errorsObject);

    if (!isEmpty(errorsObject)) {
      toast.error("Morate popuniti sva obavezna polja!", {
        theme: "colored",
      });
    }

    return !isEmpty(errorsObject);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (userValidationHandler()) {
      return;
    }

    const formData = new FormData();

    // pretvaranje locales objekta u niz za backend
    const localesArray = Object.entries(locales).map(([code, v]) => ({
      ...v,
      // ako postoji id, prosledi; ako ne, backend može generisati
      id: v.id || undefined,
    }));

    const dataForm = {
      ...data,
      locales: localesArray,
      categories: (data.categories || []).map((c) => c.id),
    };

    if (id) {
      dataForm.id = id;
    }

    // Ukloni polja za popust po difoltu
    delete dataForm.discountPrice;
    delete dataForm.discountStart;
    delete dataForm.discountEnd;

    // Ako je popust aktivan, DODAJ ih
    if (data.discount) {
      if (data.discountPrice !== "") {
        dataForm.discountPrice = data.discountPrice;
      }
      if (data.discountStart) {
        const d1 = new Date(data.discountStart);
        if (!isNaN(d1)) dataForm.discountStart = d1.toISOString();
      }
      if (data.discountEnd) {
        const d2 = new Date(data.discountEnd);
        if (!isNaN(d2)) dataForm.discountEnd = d2.toISOString();
      }
    }

    // formData.append(
    //   "section",
    //   new Blob([JSON.stringify(dataForm)], { type: "application/json" })
    // );

    dispatch(updateSection(dataForm, navigate, id));
  };

  const updateLocale = (code, field, value) => {
    setLocales((prev) => ({
      ...prev,
      [code]: { ...prev[code], [field]: value },
    }));
  };

  const handleInputChange = (eOrName, maybeValue) => {
    if (typeof eOrName === "string") {
      const name = eOrName;
      const value = maybeValue;
      setData((prev) => ({ ...prev, [name]: value }));
      return;
    }

    const e = eOrName;
    const { name, type, value, checked } = e.target;

    setData((prev) => {
      // default ažuriranje
      let next = { ...prev, [name]: type === "checkbox" ? checked : value };

      // pravila za međusobno isključive checkboxe
      if (type === "checkbox") {
        if (name === "shoppable") {
          if (checked) {
            next.free = false;
          } else {
            // ako više NIJE shoppable i NIJE free → none
            if (!next.free) {
              next.price = "";
              next.discount = false;
              next.discountPrice = "";
            }
          }
        }

        if (name === "free") {
          if (checked) {
            next.shoppable = false;
            // free režim: očisti cene/popuste
            next.price = "";
            next.discount = false;
            next.discountPrice = "";
          } else {
            // ako više NIJE free i NIJE shoppable → none
            if (!next.shoppable) {
              next.price = "";
              next.discount = false;
              next.discountPrice = "";
            }
          }
        }

        if (name === "discount") {
          // popust sme samo uz shoppable
          if (!next.shoppable && checked) {
            next.discount = false;
          }
          if (!checked) {
            // ako je isključen popust, očisti sve vrednosti vezane za popust
            next.discountPrice = "";
            next.discountStart = "";
            next.discountEnd = "";
          }
        }
      }

      return next;
    });
  };

  return (
    <Fragment>
      <Breadcrumb
        breadcrumbItems={breadcrumbItems}
        type={`${translateModule}${pageType}`}
        id={id}
        pageType={pageType}
      />
      <Container className="section-container">
        <Row>
          <Col lg={12}>
            <form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col lg={12}>
                  <div className="text-end mb-2">
                    <SubmitButtonForm id={id} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={12}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>
                          <FormTitleMain />
                        </Card.Header>
                        <Card.Body>
                          <div className="blog-holder-admin-left-side-text">
                            <Row className="mb-2">
                              <Col>
                                <SelectStatus
                                  shoppable={true}
                                  free={true}
                                  data={data}
                                  row={true}
                                  handleInputChange={handleInputChange}
                                />
                              </Col>
                            </Row>
                            <Row>
                              {data?.shoppable && (
                                <Col lg={4} md={12} xs={12} className="mb-4">
                                  <AmountInputField
                                    label={t(`${translateForm}.price`)}
                                    name="price"
                                    value={data.price}
                                    onChange={handleInputChange}
                                    error={errors}
                                    errorsHandler={setErrors}
                                    fullWidth
                                    size="small"
                                    required={data?.shoppable}
                                    inputProps={{
                                      endAdornment: (
                                        <InputAdornment position="start">
                                          RSD
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                </Col>
                              )}
                              {data?.shoppable && (
                                <Col lg={4} md={12} xs={12} className="mb-4">
                                  <InputField
                                    label={t(`${translateForm}.accessDuration`)}
                                    name="accessDuration"
                                    value={data.accessDuration}
                                    onChange={handleInputChange}
                                    error={errors}
                                    errorsHandler={setErrors}
                                    fullWidth
                                    size="small"
                                    required={data?.shoppable}
                                  />
                                </Col>
                              )}
                            </Row>
                            <Row>
                              <Col lg={12}>
                                <Card>
                                  <Card.Header>
                                    <SelectStatus
                                      discount={true}
                                      data={data}
                                      handleInputChange={handleInputChange}
                                    />
                                  </Card.Header>
                                  {data?.discount && (
                                    <Card.Body>
                                      <Row>
                                        <Col
                                          lg={4}
                                          md={12}
                                          xs={12}
                                          className="mb-4"
                                        >
                                          <AmountInputField
                                            label={t(
                                              `${translateForm}.discountPrice`
                                            )}
                                            name="discountPrice"
                                            value={data.discountPrice}
                                            onChange={handleInputChange}
                                            error={errors}
                                            errorsHandler={setErrors}
                                            fullWidth
                                            required={data?.discount}
                                            size="small"
                                            inputProps={{
                                              endAdornment: (
                                                <InputAdornment position="start">
                                                  RSD
                                                </InputAdornment>
                                              ),
                                            }}
                                          />
                                        </Col>
                                        <Col
                                          lg={4}
                                          md={12}
                                          xs={12}
                                          className="mb-4"
                                        >
                                          <InputField
                                            type="date"
                                            label={t(
                                              `${translateForm}.discountStart`
                                            )}
                                            name="discountStart"
                                            value={data?.discountStart}
                                            onChange={handleInputChange}
                                            error={errors}
                                            errorsHandler={setErrors}
                                            fullWidth
                                            required={data?.discount}
                                            size="small"
                                            inputLabelProps={{ shrink: true }}
                                            inputProps={{
                                              max:
                                                data?.discountEnd || undefined,
                                            }}
                                          />
                                        </Col>
                                        <Col
                                          lg={4}
                                          md={12}
                                          xs={12}
                                          className="mb-4"
                                        >
                                          <InputField
                                            type="date"
                                            label={t(
                                              `${translateForm}.discountEnd`
                                            )}
                                            name="discountEnd"
                                            value={data?.discountEnd}
                                            onChange={handleInputChange}
                                            error={errors}
                                            errorsHandler={setErrors}
                                            fullWidth
                                            size="small"
                                            inputLabelProps={{ shrink: true }}
                                            inputProps={{
                                              min:
                                                data?.discountStart ||
                                                undefined,
                                            }}
                                          />
                                        </Col>
                                      </Row>
                                    </Card.Body>
                                  )}
                                </Card>
                              </Col>
                            </Row>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
                {/* <Col lg={3}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>{t(`global.settings`)}</Card.Header>
                        <Card.Body>
                          <SelectStatus
                            active={true}
                            trending={true}
                            data={data}
                            handleInputChange={handleInputChange}
                          />
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col> */}
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default SectionAdd;
